import React, { useState } from 'react';
import { 
  Lock, ArrowRight, ArrowDown, CheckCircle2, XCircle, 
  UserPlus, MailCheck, Briefcase, FileSignature, Users, Zap, 
  ChevronDown, ChevronUp, Linkedin, Twitter, Target, Lightbulb, BellDot
} from 'lucide-react';
import EarlyAccessBenefitsSection from './EarlyAccessBenefitsSection';
import NexGentShowcase from './NexGentShowcase';
import { LandingHeader } from './LandingHeader';
import { useRedirect } from '../../Shared/Hooks/useRedirect ';
import { LandingFooter } from './LandingFooter';

// Define the core features list for reuse
const features = [
  { text: 'Invitation Only', icon: Lock },
  { text: 'Leadership Focused', icon: Target },
  { text: 'Quality Connections', icon: CheckCircle2 },
  { text: 'Privacy First', icon: Lock }
];

const faqData = [
    {
      question: "What is NexGent?",
      answer:
        "NexGent is a private leadership hiring Platform built for experienced professionals and decision-makers. We created it because senior leaders are tired of noisy job portals, irrelevant outreach, and opportunities that never match their level of impact. Every connection on NexGent is designed to be more intentional, relevant, and confidential."
    },
  
    {
      question: "How do I get an invitation?",
      answer:
        "Access is currently invitation-based to maintain the quality of the platform. Invitations are shared by participating companies, hiring leaders, and trusted partners. If you haven't received one yet, you can join the waitlist and we'll review your profile as new openings become available."
    },
  
    {
      question: "Can I apply to jobs directly?",
      answer:
        "No. NexGent works differently from traditional job boards. Instead of competing against hundreds of applicants, qualified professionals are invited to opportunities that align with their experience, leadership background, and career goals. This creates more meaningful conversations and better outcomes for both sides."
    },
  
    {
      question: "Is my information secure?",
      answer:
        "Absolutely. Many senior professionals are open to the right opportunity but cannot afford to publicly signal that they are exploring options. Your profile remains private by default, and we prioritize confidentiality, discretion, and data security at every stage."
    },
  
    {
      question: "Who can see my profile?",
      answer:
        "Your profile is never publicly searchable. It is only shared with carefully selected hiring teams and decision-makers when there is a strong match between your experience and a leadership opportunity."
    },
  
    {
      question: "Can I invite others?",
      answer:
        "At the moment, invitations are controlled to preserve the quality of the platform. As NexGent grows, selected members may receive the ability to recommend exceptional leaders and trusted hiring partners."
    },
  
    {
      question: "How is NexGent different from LinkedIn or traditional job portals?",
      answer:
        "Most platforms are built for volume. NexGent is built for relevance. Instead of endless applications, recruiter spam, and public visibility, we focus on curated leadership opportunities, trusted introductions, and confidential hiring conversations. It's designed for executives, senior managers, founders, and hiring leaders who value quality over noise."
    },
  
    {
      question: "Why was NexGent created?",
      answer:
        "Because leadership hiring is broken. Great leaders often never see the right opportunities, while companies struggle to identify trusted candidates beyond their immediate network. NexGent bridges that gap by creating a private ecosystem where exceptional talent and forward-thinking organizations can connect with confidence."
    },
  
    {
      question: "When will NexGent be available to everyone?",
      answer:
        "Our goal is not to become another public job portal. NexGent is intentionally designed as a private network because exclusivity, trust, and relevance are what make leadership hiring work. Maintaining that quality will always be our priority."
    }
  ];

const ComparisonTable = () => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 text-center px-4 md:px-0">
    <div className="border border-slate-700 bg-slate-800/40 p-6 md:p-8 rounded-2xl">
      <h4 className="text-xl md:text-2xl font-bold text-slate-100 mb-6">Traditional Job Portals</h4>
      <ul className="space-y-4 text-left text-slate-400">
        {[
          "Anyone can register",
          "Public job postings",
          "Irrelevant applications",
          "Mass outreach & spam",
          "High noise, low quality",
          "Hard to find the right leaders",
          "No privacy control"
        ].map(item => (
          <li key={item} className="flex items-center gap-3"><XCircle className="w-5 h-5 text-red-500" /> {item}</li>
        ))}
      </ul>
    </div>
    <div className="border-4 border-cyan-500 bg-slate-900 p-6 md:p-8 rounded-2xl shadow-2xl relative order-first lg:order-none">
      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-cyan-500 text-slate-950 font-bold px-4 py-1 rounded-full text-xs uppercase">Your private edge</div>
      <h4 className="text-2xl md:text-3xl font-extrabold text-cyan-400 mb-6 flex items-center justify-center gap-3"><Lock className="w-7 h-7"/>NexGent</h4>
      <ul className="space-y-4 text-left text-cyan-100">
         {[
          "Invitation-only access",
          "Jobs shared by invitation",
          "Pre-qualified & relevant connects",
          "Direct engagement",
          "High-quality, contextual matches",
          "Built for leadership hiring",
          "Privacy & control in your hands"
        ].map(item => (
          <li key={item} className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-cyan-400" /> {item}</li>
        ))}
      </ul>
    </div>
    <div className="border border-slate-700 bg-slate-800/40 p-6 md:p-8 rounded-2xl">
      <h4 className="text-xl md:text-2xl font-bold text-slate-100 mb-6">Benefit to You</h4>
      <ul className="space-y-4 text-left text-slate-400">
         {[
          "Trusted Platform",
          "Save time & effort",
          "Better quality conversations",
          "Higher response rate",
          "Faster hiring outcomes",
          "Access to leadership talent",
          "Secure & professional experience"
        ].map(item => (
          <li key={item} className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-slate-500" /> {item}</li>
        ))}
      </ul>
    </div>
  </div>
);

// Illustrative Desktop Screenshot Content
const DesktopScreenshot = () => (
  <div className="w-full h-full bg-slate-800 rounded-lg p-6 font-mono text-[11px] text-slate-500">
    <div className="flex items-center gap-3 border-b border-slate-700 pb-4 mb-4">
      <div className="w-8 h-8 bg-cyan-600 rounded-lg flex items-center justify-center font-bold text-slate-950 text-xl">N</div>
      <span className="text-xl font-bold text-slate-300">NexGent</span>
      <div className="flex-1 text-right text-slate-600">Search for talent...</div>
    </div>
    <div className="grid grid-cols-4 gap-4">
      <div className="space-y-3">
        <div className="bg-slate-700 p-3 rounded">Dashboard</div>
        <div className="text-cyan-400 font-semibold p-1">Candidates</div>
        {['Messages', 'Settings', 'Profile'].map(i => <div key={i}>{i}</div>)}
      </div>
      <div className="col-span-3 border border-slate-700 p-4 rounded-xl space-y-4">
        <h4 className="text-sm font-semibold text-slate-300">New Leadership Roles (2)</h4>
        <div className="flex items-center gap-4 border border-cyan-800 bg-cyan-950/30 p-3 rounded-lg text-cyan-200">
          <Briefcase className="w-6 h-6 text-cyan-400" />
          <div>
            VP Engineering - Global FinTech Corp.
            <div className="text-slate-500">Shared: 1 hr ago</div>
          </div>
          <button className="bg-cyan-500 text-slate-950 px-3 py-1 text-xs rounded-full font-bold ml-auto">View & Apply</button>
        </div>
         <div className="flex items-center gap-4 border border-slate-700 bg-slate-800 p-3 rounded-lg text-slate-300">
          <Briefcase className="w-6 h-6 text-slate-500" />
          <div>
            Chief Product Officer - AI Health Startup
            <div className="text-slate-500">Shared: 3 days ago</div>
          </div>
          <button className="border border-slate-600 text-slate-400 px-3 py-1 text-xs rounded-full ml-auto">Pending</button>
        </div>
      </div>
    </div>
  </div>
);

// Illustrative Mobile Screenshot Content
const MobileScreenshot = () => (
  <div className="w-full h-full bg-slate-800 rounded-2xl p-4 font-mono text-[10px] text-slate-500">
    <div className="flex items-center justify-between border-b border-slate-700 pb-3 mb-3">
        <span className="text-xs font-bold text-slate-300">NexGent</span>
        <BellDot className="w-4 h-4 text-cyan-500" />
    </div>
    <div className="space-y-3">
        <h4 className="text-sm font-semibold text-slate-200">Welcome, Elena!</h4>
        <div className="flex items-center gap-3 border border-slate-700 bg-slate-800 p-3 rounded-lg text-slate-300">
          <Briefcase className="w-5 h-5 text-slate-500" />
          <div>
            Product Director...
            <div className="text-slate-600">Pending Invite</div>
          </div>
        </div>
        <div className="bg-slate-700 h-20 rounded-xl flex items-center justify-center text-slate-400 text-xs">Profile Activity (Chart)</div>
        <button className="bg-cyan-500 text-slate-950 w-full py-2 rounded-lg font-bold">Manage Profile</button>
    </div>
  </div>
);

// Specific illustations for early access section
const OpportunityIcon = () => (
    <div className="border-4 border-cyan-500 bg-slate-950 p-6 rounded-3xl w-24 h-24 flex items-center justify-center text-cyan-400 mx-auto">
        <Zap className="w-12 h-12" />
    </div>
);
const LeadershipIcon = () => (
    <div className="border-4 border-cyan-500 bg-slate-950 p-6 rounded-3xl w-24 h-24 flex items-center justify-center text-cyan-400 mx-auto">
        <Users className="w-12 h-12" />
    </div>
);
const ShapingIcon = () => (
    <div className="border-4 border-cyan-500 bg-slate-950 p-6 rounded-3xl w-24 h-24 flex items-center justify-center text-cyan-400 mx-auto">
        <Lightbulb className="w-12 h-12" />
    </div>
);

function SharedFeatureIcons() {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8 text-center border-t border-slate-800/80 w-full">
        {[
          { icon: OpportunityIcon, label: 'Access Opportunities' },
          { icon: LeadershipIcon, label: 'Trusted Network' },
          { icon: ShapingIcon, label: 'Shape The Future' }
        ].map(i => (
          <div key={i.label} className="space-y-3 p-3 rounded-xl bg-slate-950/40 border border-slate-800/40 hover:bg-slate-950/80 transition-colors group/icon">
            <div className="flex justify-center text-cyan-400 group-hover/icon:text-cyan-300 transition-colors">
              <i.icon />
            </div>
            <p className="text-xs text-slate-400 font-mono tracking-wide">{i.label}</p>
          </div>
        ))}
      </div>
    );
  }

export const DetailsNexGentLandingPage = () => {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const isEarlyAccessMode = process.env.REACT_APP_SHOW_EARLY_ACCESS_MODE === "true";


  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const redirect = useRedirect();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans antialiased overflow-x-hidden">
      <LandingHeader/>
      <main className="container mx-auto px-4 md:px-6 py-12 md:py-20 space-y-24 md:space-y-32">
        {/* Hero Section */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
  <div className="lg:col-span-6 space-y-6 md:space-y-8 text-center lg:text-left">
    <div className="inline-flex items-center gap-2 border border-cyan-800 bg-cyan-950/50 text-cyan-300 px-4 py-1.5 rounded-full text-[10px] md:text-xs font-medium uppercase tracking-wider">
      <Lock className="w-3.5 h-3.5" /> EARLY ACCESS - INVITATION ONLY
    </div>
    
    <h1 className="text-4xl md:text-6xl font-extrabold text-slate-50 leading-tight">
      The Private <span className="text-cyan-400">Leadership</span> Hiring Platform
    </h1>
    
    <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto lg:mx-0">
      NexGent connects experienced leaders with forward-thinking organizations through trusted, meaningful connections.
    </p>
    
    <div className="flex flex-wrap gap-x-8 gap-y-3 text-white-500 text-sm justify-center lg:justify-start">
      {features.map((feature, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <feature.icon className="w-4 h-4 text-cyan-600" /> {feature.text}
        </div>
      ))}
    </div>
    
    <div className="flex flex-col sm:flex-row items-center gap-4 pt-4 justify-center lg:justify-start">
      <button onClick={()=>
        // redirect("early-access")
        redirect(isEarlyAccessMode ? "early-access" : "prelaunch")
        } className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-bold px-8 py-3.5 rounded-lg transition-colors text-lg w-full sm:w-auto">
        Request Early Access
      </button>
      {/* <button className="border border-slate-700 hover:border-slate-600 bg-slate-800 text-slate-100 px-8 py-3.5 rounded-lg transition-colors text-lg w-full sm:w-auto">
        Learn More
      </button> */}
    </div>
    
    <p className="text-xs md:text-sm text-slate-200/70 pt-2 flex items-center justify-center lg:justify-start gap-2">
      <Lock className="w-4 h-4"/> Access by invitation only. Join the waitlist to get early access.
    </p>
  </div>

  <div className="hidden lg:col-span-6 lg:block relative w-full px-4 lg:px-0 mt-0">
  <NexGentShowcase />
</div>
</section>


{/* Middle Hero Section */}
<section className="relative overflow-hidden bg-slate-950  border-b border-slate-900 mt-0">
    {/* Background Decorative Elements */}
    <div className="absolute top-0 -z-10 h-full w-full bg-slate-950">
      {/* Cybernetic Dark Glow */}
      <div className="absolute left-1/2 top-0 h-[800px] w-[800px] -translate-x-1/2 rounded-full bg-cyan-950/20 blur-3xl opacity-60" />
    </div>

    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
      {/* Standard div replacing motion.div */}
      <div className="opacity-100"> 
        {/* Neon Accent Badge */}
        <span className="inline-block rounded-full bg-cyan-950/40 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-cyan-400 ring-1 ring-inset ring-cyan-500/30 backdrop-blur-sm">
          The Future of Hiring
        </span>
        
        {/* Stark Main Heading. This is the second hero on the page, so it stays an h2 - the page
            keeps exactly one h1, the one in the hero above. */}
        <h2 className="mt-8 text-5xl font-extrabold tracking-tight text-white sm:text-7xl">
          Stop applying. <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-teal-400 to-indigo-400">
            Start being recruited.
          </span>
        </h2>
        
        {/* Subtext */}
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-slate-400">
          Flip the script on the job hunt. Create your profile, set your terms, and let 
          top-tier engineering teams apply to hire <span className="font-semibold text-cyan-400">you</span>.
        </p>
        
        {/* Cyber Interactive Buttons */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button onClick={()=>
            // redirect("early-access")
            redirect(isEarlyAccessMode ? "early-access" : "prelaunch")
            } className="w-full sm:w-auto rounded-xl bg-cyan-500 px-8 py-4 text-sm font-bold text-slate-950 shadow-lg shadow-cyan-500/20 hover:bg-cyan-400 transition-all hover:-translate-y-0.5 hover:shadow-cyan-400/30">
            Build Your Profile
          </button>
          <button 
          onClick={() => {
            document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
          }}
            className="w-full sm:w-auto rounded-xl border border-slate-800 bg-slate-900 px-8 py-4 text-sm font-bold text-slate-300 hover:bg-slate-800 hover:text-white hover:border-slate-700 transition-all">
            See How It Works
          </button>
        </div>
      </div>
    </div>
  </section>
 


            {/* Early Access Benefits Section */}
           <EarlyAccessBenefitsSection/>

                {/* Streamlined Final CTA */}
        <section className="border border-cyan-800 bg-cyan-950 p-8 md:p-10 rounded-3xl text-center md:text-left shadow-2xl flex flex-col md:flex-row items-center md:items-center md:justify-between gap-8 md:gap-12 lg:gap-16 px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center md:items-center gap-6 md:gap-8 flex-1">
             <div className="bg-slate-950 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto md:mx-0 border border-cyan-700 flex-shrink-0">
                <MailCheck className="w-10 h-10 text-cyan-400" />
              </div>
              <div className="space-y-3">
                  <h2 className="text-3xl font-extrabold text-slate-50 leading-snug">Be Part of the Future of Leadership Hiring</h2>
                  <p className="text-lg text-cyan-100 max-w-xl mx-auto md:mx-0">
                    NexGent is invite-only and early access is limited. Request an invitation and be part of a trusted community.
                  </p>
                  <p className="text-xs text-cyan-400/80 font-medium tracking-wide flex items-center justify-center md:justify-start gap-2 pt-1"><Lock className="w-3.5 h-3.5"/> Access by invitation only</p>
              </div>
          </div>
          <div className="flex items-center gap-4 pt-4 md:pt-0 justify-center w-full sm:w-auto flex-shrink-0">
              <button onClick={()=>
                // redirect("early-access")
                redirect(isEarlyAccessMode ? "early-access" : "prelaunch")
                } className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-bold px-10 md:px-12 py-3.5 md:py-4 rounded-lg transition-colors text-lg w-full sm:w-auto">
                Request Early Access
              </button>
          </div>
        </section>



        {/* What & Why Section */}
        <section className="grid lg:grid-cols-2 gap-16 md:gap-20 items-start">
          <div className="space-y-10">
            <h2 className="text-4xl font-bold text-slate-100 mb-8 text-center lg:text-left">What is NexGent?</h2>
            <p className="text-lg md:text-xl text-slate-400 mb-10 leading-relaxed text-center lg:text-left max-w-2xl mx-auto lg:mx-0">
              NexGent is a private, invitation-only platform built for leadership hiring. We bring together experienced professionals and organizations that value quality, privacy, and meaningful connections over volume.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                    {icon: Lock, title: "Private & Secure", text: "Your data and profile are always protected."},
                    {icon: Users, title: "Leadership Focused", text: "Built exclusively for senior and leadership roles."},
                    {icon: Target, title: "Direct Connections", text: "Connect directly with the right people, faster."},
                    {icon: Zap, title: "Quality Over Quantity", text: "We believe in the right fit, not endless applications."}
                ].map(item => (
                    <div key={item.title} className="flex items-start gap-4 p-5 border border-slate-800 bg-slate-900 rounded-xl">
                        <div className="bg-slate-800 w-12 h-12 rounded-lg flex-shrink-0 flex items-center justify-center border border-cyan-900"><item.icon className="w-6 h-6 text-cyan-400" /></div>
                        <div>
                            <h4 className="font-semibold text-slate-100 text-base md:text-lg">{item.title}</h4>
                            <p className="text-sm text-slate-500">{item.text}</p>
                        </div>
                    </div>
                ))}
            </div>
          </div>
          <div className="border border-slate-700 bg-slate-800/50 p-10 md:p-12 rounded-3xl relative mt-16 lg:mt-0">
            <div className="bg-cyan-500 w-16 h-16 rounded-full flex items-center justify-center absolute -top-8 -left-8 shadow-xl"><Lock className="w-8 h-8 text-slate-950"/></div>
            <h3 className="text-3xl font-bold text-cyan-400 mb-6">Why We Built NexGent?</h3>
            <ul className="space-y-5 text-slate-300 list-disc list-inside text-lg leading-relaxed">
                <li>Traditional job portals are noisy and cluttered.</li>
                <li>Leadership roles need discretion, context, and trust.</li>
                <li>Recruiters spend hours searching, but still miss the right leaders.</li>
                <li>Candidates are tired of irrelevant opportunities and spam.</li>
            </ul>
            <div className="mt-10 border border-cyan-800/50 bg-cyan-950/40 p-6 rounded-2xl flex items-center gap-4 shadow-inner">
                <Target className="w-10 h-10 text-cyan-300 flex-shrink-0"/>
                <p className="text-cyan-100 font-medium leading-snug">NexGent is our answer to make leadership hiring smarter, faster, and more meaningful.</p>
            </div>
          </div>
        </section>



   

        {/* How It Works */}
        <section id="how-it-works">
          <h2 className="text-4xl font-bold text-slate-100 text-center mb-6">How NexGent Works (Invite Based)</h2>
          <p className="text-xl md:text-2xl text-slate-400 text-center mb-16 max-w-2xl mx-auto">A simple, private, and efficient way to connect.</p>
          <div className="flex flex-col lg:flex-row items-stretch lg:items-start lg:justify-between lg:gap-4 relative text-center px-4 md:px-0">
            {[
              { icon: UserPlus, title: 'Invitation', text: 'Invitations are sent by organizations to candidates or recruiters.' },
              { icon: MailCheck, title: 'Register & Verify', text: 'Invitees register, verify their email, and complete their profile.' },
              { icon: Briefcase, title: 'Jobs Shared by Invitation', text: 'Organizations invite you to specific leadership roles.' },
              { icon: FileSignature, title: 'Connect & Engage', text: 'If interested, both sides connect and start a meaningful conversation.' },
              { icon: CheckCircle2, title: 'Right Fit, Faster', text: 'We help organizations hire the right leaders, and leaders build what\'s next.' },
            ].map((step, index) => (
              <React.Fragment key={step.title}>
                  <div className="border border-slate-700 bg-slate-900 p-8 rounded-2xl flex flex-col items-center flex-1 justify-center relative z-10 shadow-lg min-h-[300px]">
                    <div className="bg-slate-800 w-16 h-16 rounded-full flex items-center justify-center border border-cyan-800 mb-6">
                      <step.icon className="w-8 h-8 text-cyan-400" />
                    </div>
                    <h4 className="font-bold text-slate-100 text-lg md:text-xl mb-3">{step.title}</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">{step.text}</p>
                    <div className="absolute top-4 left-4 bg-slate-700 text-cyan-300 font-mono text-xs w-6 h-6 rounded-full flex items-center justify-center border border-cyan-800">0{index + 1}</div>
                  </div>
                  {index < 4 && <ArrowDown className="w-8 h-8 text-slate-600 self-center hidden lg:block rotate-[-90deg] mx-1 mt-[130px] flex-shrink-0" />}
                   {index < 4 && <ArrowDown className="w-8 h-8 text-slate-600 self-center my-6 block lg:hidden" />}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-20 md:mt-16 text-center border-y border-x-0 sm:border md:rounded-xl border-cyan-900 bg-cyan-950/20 px-6 py-6 flex flex-col sm:flex-row items-center justify-center gap-4 w-full">
  <Lock className="w-5 h-5 text-cyan-400 flex-shrink-0" />
  <p className="text-cyan-100 font-medium text-sm leading-relaxed max-w-3xl">
    <span className="font-bold block sm:inline mb-1 sm:mb-0">No Public Job Postings. No Unwanted Applications.</span> Every opportunity on NexGent is shared by invitation only, ensuring relevance and respect for your time.
  </p>
</div>
    
        </section>

        {/* How is NexGent Different */}
        <section>
          <h2 className="text-4xl font-bold text-slate-100 text-center mb-6">How NexGent is Different?</h2>
          <p className="text-xl md:text-2xl text-slate-400 text-center mb-16 max-w-xl mx-auto px-4 md:px-0">We're not just another job portal. We are a private leadership platform.</p>
          <ComparisonTable />
        </section>

        {/* Who is NexGent For */}
        <section>
          <h2 className="text-4xl font-bold text-slate-100 text-center mb-16 md:mb-20">Who is NexGent For?</h2>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 px-4 md:px-0">
            <div className="border border-slate-700 bg-slate-900 p-10 md:p-12 rounded-3xl">
              <div className="bg-cyan-950 w-20 h-20 rounded-2xl flex items-center justify-center border-2 border-cyan-700 mb-8 mx-auto">
                <Users className="w-10 h-10 text-cyan-300" />
              </div>
              <h4 className="text-2xl md:text-3xl font-bold text-slate-100 mb-8 md:mb-10 text-center">For Candidates</h4>
              <ul className="space-y-4 md:space-y-5 text-slate-400 text-lg leading-relaxed">
                <li className="flex items-start gap-3"><CheckCircle2 className="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5"/>Leaders looking for meaningful opportunities</li>
                <li className="flex items-start gap-3"><CheckCircle2 className="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5"/>Professionals who value privacy</li>
                <li className="flex items-start gap-3"><CheckCircle2 className="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5"/>Experts who are open to better roles</li>
                <li className="flex items-start gap-3"><CheckCircle2 className="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5"/>People who want to be considered for the right roles</li>
              </ul>
              <p className="text-cyan-200 mt-10 md:mt-12 text-center font-medium leading-snug">Get invited. Get noticed. Get the right opportunity.</p>
            </div>
            <div className="border border-slate-700 bg-slate-900 p-10 md:p-12 rounded-3xl">
               <div className="bg-cyan-950 w-20 h-20 rounded-2xl flex items-center justify-center border-2 border-cyan-700 mb-8 mx-auto">
                <Briefcase className="w-10 h-10 text-cyan-300" />
              </div>
              <h4 className="text-2xl md:text-3xl font-bold text-slate-100 mb-8 md:mb-10 text-center">For Recruiters / Organizations</h4>
              <ul className="space-y-4 md:space-y-5 text-slate-400 text-lg leading-relaxed">
                <li className="flex items-start gap-3"><CheckCircle2 className="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5"/>Companies hiring for leadership and critical roles</li>
                <li className="flex items-start gap-3"><CheckCircle2 className="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5"/>Recruiters and headhunters</li>
                <li className="flex items-start gap-3"><CheckCircle2 className="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5"/>Teams building high-performing leadership groups</li>
                <li className="flex items-start gap-3"><CheckCircle2 className="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5"/>Organizations that value quality over quantity</li>
              </ul>
              <p className="text-cyan-200 mt-10 md:mt-12 text-center font-medium leading-snug">Invite the right leaders. Build what's next.</p>
            </div>
          </div>
        </section>

        {/* Why Join Early Access & FAQ */}
        <section className="border border-cyan-800 bg-light-purple-950 p-8 md:p-10 rounded-3xl text-center md:text-left shadow-2xl flex flex-col md:flex-row items-center md:items-center md:justify-between gap-8 md:gap-12 lg:gap-16 px-6 md:px-12">
          {/* <div className="lg:col-span-5 border border-slate-700 bg-slate-800/40 p-10 md:p-12 rounded-3xl relative space-y-10">
            <h3 className="text-3xl font-bold text-slate-50 text-center lg:text-left">Why Join Early Access?</h3>
            <p className="text-lg md:text-xl text-slate-400 leading-relaxed text-center lg:text-left max-w-xl mx-auto lg:mx-0">We are in early access to build the best experience for both leaders and organizations. As an early member, you get:</p>
            <ul className="space-y-4 text-slate-300 text-lg leading-relaxed">
                {[
                    'Early access to the platform',
                    'Be part of a trusted leadership network',
                    'Help shape the future of leadership hiring',
                    'Priority support and guidance',
                    'A better way to hire or grow your career'
                ].map(item => (
                    <li key={item} className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-cyan-500 flex-shrink-0" /> {item}</li>
                ))}
            </ul> */}
            {/* Illustrative Icons based on reference */}
             {/* <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-10 text-center">
                {[
                    {icon: OpportunityIcon, label: 'Access Opportunities'},
                    {icon: LeadershipIcon, label: 'Trusted Network'},
                    {icon: ShapingIcon, label: 'Shape The Future'}
                ].map(i => (
                    <div key={i.label} className="space-y-3">
                        <i.icon />
                        <p className="text-xs text-slate-500 font-mono tracking-wide">{i.label}</p>
                    </div>
                ))}
             </div>
          </div> */}
          
          {/* <div className="lg:col-span-7 mt-16 lg:mt-0 ">
            <h2 className="text-4xl font-bold text-slate-100 mb-10 md:mb-12 text-center lg:text-center">Frequently Asked Questions</h2>
            <div className="space-y-4 max-w-3xl mx-auto lg:mx-0">
              {faqData.map((faq, index) => (
                <div key={index} className="border border-slate-700 bg-slate-900 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full text-left px-6 py-4 flex items-center justify-between font-medium text-slate-100 hover:text-cyan-400 transition-colors"
                  >
                    {faq.question}
                    {activeFaq === index ? <ChevronUp className="w-5 h-5 text-cyan-500 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-cyan-500 flex-shrink-0" />}
                  </button>
                  <div className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${activeFaq === index ? 'max-h-96 py-4 md:py-6 border-t border-slate-800' : 'max-h-0'}`}>
                    <p className="text-slate-400 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div> */}

          {/* Changed from lg:col-span-7 to lg:col-span-12 and added w-full */}
          <div className="lg:col-span-12 mt-16 lg:mt-0 w-full">
            <h2 className="text-4xl font-bold text-slate-100 mb-10 md:mb-12 text-center">
              Frequently Asked Questions
            </h2>
            {/* Changed max-w-3xl to max-w-full and updated margins for full-width layout */}
            <div className="space-y-4 max-w-full mx-auto">
              {faqData.map((faq, index) => (
                <div key={index} className="border border-slate-700 bg-slate-900 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full text-left px-6 py-4 flex items-center justify-between font-medium text-slate-100 hover:text-cyan-400 transition-colors"
                  >
                    {faq.question}
                    {activeFaq === index ? <ChevronUp className="w-5 h-5 text-cyan-500 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-cyan-500 flex-shrink-0" />}
                  </button>
                  <div className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${activeFaq === index ? 'max-h-96 py-4 md:py-6 border-t border-slate-800' : 'max-h-0'}`}>
                    <p className="text-slate-400 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>

      <LandingFooter/>
      
    </div>
  );
};

export default DetailsNexGentLandingPage;